import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CalendarCheck, Menu, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Navigation copy per language. NAV_KEYS index is aligned with `nav` array.
const NAV_COPY = {
  tr: { nav: ['Ana Sayfa', 'Hakkimizda', 'Hizmetler', 'Paketler', 'Galeri', 'Fiyatlar', 'Iletisim'], book: 'Randevu Al' },
  en: { nav: ['Home', 'About', 'Services', 'Packages', 'Gallery', 'Pricing', 'Contact'], book: 'Book Now' },
  ru: { nav: ['Главная', 'О нас', 'Услуги', 'Пакеты', 'Галерея', 'Цены', 'Контакты'], book: 'Записаться' },
  no: { nav: ['Hjem', 'Om oss', 'Tjenester', 'Pakker', 'Galleri', 'Priser', 'Kontakt'], book: 'Bestill time' },
  de: { nav: ['Startseite', 'Über uns', 'Leistungen', 'Pakete', 'Galerie', 'Preise', 'Kontakt'], book: 'Termin buchen' },
};

export const NAV_KEYS = ['home', 'about', 'services', 'packages', 'gallery', 'pricing', 'contact'];

// Routing rule per nav key. "route" navigates with React Router; "anchor" scrolls to
// a section on Home (or routes to /#anchor from inner pages). Pricing intentionally
// points to the Packages page per product decision.
const NAV_TARGETS = {
  home: { type: 'anchor', value: 'home' },
  about: { type: 'route', value: '/about' },
  services: { type: 'route', value: '/services' },
  packages: { type: 'route', value: '/packages' },
  gallery: { type: 'anchor', value: 'gallery' },
  pricing: { type: 'route', value: '/packages' },
  contact: { type: 'route', value: '/contact' },
};

const getNavCopy = (lang) => NAV_COPY[lang] || NAV_COPY.en;

const Navbar = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const t = getNavCopy(currentLanguage);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const renderNavLink = (id, label, extraClass = '', onClick) => {
    const target = NAV_TARGETS[id];
    if (!target) return null;

    if (target.type === 'route') {
      return (
        <Link key={id} to={target.value} className={extraClass} onClick={onClick}>
          {label}
        </Link>
      );
    }

    const href = isHome ? `#${target.value}` : `/#${target.value}`;
    return (
      <a key={id} href={href} className={extraClass} onClick={onClick}>
        {label}
      </a>
    );
  };

  return (
    <>
      {/* Mobile menu */}
      <div className={`mobile-menu ${mobileOpen ? 'active' : ''}`}>
        <button
          className="mobile-menu-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
        {NAV_KEYS.map((id, i) =>
          renderNavLink(id, t.nav[i], '', () => setMobileOpen(false))
        )}
        <Link
          to="/appointment"
          className="mobile-menu-cta"
          onClick={() => setMobileOpen(false)}
        >
          <CalendarCheck size={16} /> {t.book}
        </Link>
      </div>

      {/* Desktop / top header */}
      <header className={`navbar-crystal ${scrolled || !isHome ? 'scrolled' : ''}`}>
        <div className="container-premium nav-inner">
          <Link to="/" className="brand">
            <img src="/images/logo.png" alt="Crystal Dental" className="logo-img" />
            <span className="brand-text">Crystal Dental</span>
          </Link>

          <nav className="desktop-nav">
            {NAV_KEYS.map((id, i) => renderNavLink(id, t.nav[i], 'nav-link-crystal'))}
          </nav>

          <div className="nav-actions">
            <select
              className="lang-select"
              value={currentLanguage}
              onChange={(e) => changeLanguage(e.target.value)}
              aria-label="Language"
            >
              <option value="tr">🇹🇷 Turkce</option>
              <option value="en">🇬🇧 English</option>
              <option value="ru">🇷🇺 Russkiy</option>
              <option value="no">🇳🇴 Norsk</option>
              <option value="de">🇩🇪 Deutsch</option>
            </select>
            <Link to="/appointment" className="nav-book-btn">
              <CalendarCheck size={15} />
              <span>{t.book}</span>
            </Link>
            <button
              className="menu-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
